// AccountCombobox - Autocomplete combobox for account selection
//
// Shows a text input that:
// - Displays the current account name (from LLM extraction)
// - On focus/typing: shows a filtered dropdown of matching accounts
// - If an existing account matches: auto-select with green checkmark
// - If no match: show typed text with "(New)" badge, keep account_uuid null
// - Selecting an account sets both account_name and account_uuid

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useAccounts } from '../hooks/useApi';
import { useClickOutside } from '../../hooks/useClickOutside';
import type { AccountListItem } from '../types/account';
import styles from './AccountCombobox.module.css';

interface AccountComboboxProps {
  value: string;
  accountUuid: string | null;
  onChange: (name: string, uuid: string | null) => void;
  label?: string;
  placeholder?: string;
}

export function AccountCombobox({
  value,
  accountUuid,
  onChange,
  label = 'Account',
  placeholder = 'Type account name...',
}: AccountComboboxProps) {
  const { accounts, loading } = useAccounts();
  const [inputValue, setInputValue] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync inputValue when parent value changes (e.g., initial load from extraction)
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Filter accounts by typed text
  const filtered = useMemo(() => {
    if (!inputValue.trim()) return accounts;
    const q = inputValue.toLowerCase();
    return accounts.filter((a) => a.display_name.toLowerCase().includes(q));
  }, [accounts, inputValue]);

  // Check if current value exactly matches an existing account
  const matchedAccount = useMemo<AccountListItem | null>(() => {
    if (!inputValue.trim()) return null;
    const q = inputValue.toLowerCase().trim();
    return accounts.find((a) => a.display_name.toLowerCase() === q) ?? null;
  }, [accounts, inputValue]);

  // Auto-select on initial load when extracted name matches an existing account
  useEffect(() => {
    if (matchedAccount && accountUuid !== matchedAccount.uuid) {
      onChange(matchedAccount.display_name, matchedAccount.uuid);
    }
  }, [matchedAccount]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeDropdown = useCallback(() => setIsOpen(false), []);
  useClickOutside(wrapperRef, closeDropdown, isOpen);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const text = e.target.value;
      setInputValue(text);
      setIsOpen(true);
      setHighlightIndex(-1);
      // If no exact match, set uuid to null
      const match = accounts.find(
        (a) => a.display_name.toLowerCase() === text.toLowerCase().trim()
      );
      onChange(text, match?.uuid ?? null);
    },
    [accounts, onChange]
  );

  const handleSelect = useCallback(
    (account: AccountListItem) => {
      setInputValue(account.display_name);
      onChange(account.display_name, account.uuid);
      setIsOpen(false);
      inputRef.current?.blur();
    },
    [onChange]
  );

  const handleFocus = useCallback(() => {
    setIsOpen(true);
    setHighlightIndex(-1);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen) {
        if (e.key === 'ArrowDown' || e.key === 'Enter') {
          setIsOpen(true);
          e.preventDefault();
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightIndex((prev) =>
            prev < filtered.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightIndex((prev) =>
            prev > 0 ? prev - 1 : filtered.length - 1
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightIndex >= 0 && highlightIndex < filtered.length) {
            handleSelect(filtered[highlightIndex]);
          } else {
            setIsOpen(false);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          break;
      }
    },
    [isOpen, filtered, highlightIndex, handleSelect]
  );

  // Determine status indicator
  const isMatched = accountUuid !== null;
  const showNew = inputValue.trim().length > 0 && !isMatched;

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      {label && <label className={styles.label}>{label}</label>}
      <div className={styles.inputRow}>
        <input
          ref={inputRef}
          className={styles.input}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
          aria-haspopup="listbox"
        />
        <span className={styles.statusIcon}>
          {isMatched && (
            <svg
              className={styles.checkmark}
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          )}
          {showNew && <span className={styles.newBadge}>(New)</span>}
        </span>
      </div>

      {isOpen && (
        <div className={styles.dropdown} role="listbox">
          {loading && <div className={styles.loading}>Loading accounts...</div>}
          {!loading && filtered.length === 0 && (
            <div className={styles.emptyState}>No matching accounts</div>
          )}
          {!loading &&
            filtered.map((account, idx) => {
              const isSelected = account.uuid === accountUuid;
              const isHighlighted = idx === highlightIndex;
              const location = [account.city, account.state]
                .filter(Boolean)
                .join(', ');
              return (
                <button
                  key={account.uuid}
                  className={`${styles.option} ${
                    isSelected ? styles.optionSelected : ''
                  } ${isHighlighted ? styles.optionHighlighted : ''}`}
                  onClick={() => handleSelect(account)}
                  role="option"
                  aria-selected={isSelected}
                  type="button"
                >
                  <span className={styles.optionName}>{account.display_name}</span>
                  {location && (
                    <span className={styles.optionLocation}>{location}</span>
                  )}
                </button>
              );
            })}
        </div>
      )}
    </div>
  );
}
